import { useState } from 'react';

const PrivacyPolicy = () => {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const sections = [
    {
      id: 'data-collection',
      title: 'Data Collection',
      content: 'Sentivo collects and processes data stored on your local Mac device to provide search functionality. We do not collect, store, or transmit any personal data to external servers. All processing happens locally on your device.'
    },
    {
      id: 'data-usage',
      title: 'How We Use Your Data',
      content: 'Your data is used exclusively to power the search functionality within Sentivo. We index file metadata, content, and location information to deliver fast, accurate search results. No data is shared with third parties or used for any other purposes.'
    },
    {
      id: 'data-storage',
      title: 'Data Storage',
      content: 'All indexed data and search indices are stored locally on your Mac. Sentivo maintains a local database to ensure fast search performance without compromising your privacy. You retain full control over your data at all times.'
    },
    {
      id: 'data-security',
      title: 'Data Security',
      content: 'We implement industry-standard security practices to protect your locally stored data. Sentivo uses encryption for sensitive data and follows macOS security guidelines to ensure your information remains secure.'
    },
    {
      id: 'third-party',
      title: 'Third-Party Services',
      content: 'Sentivo may integrate with third-party services for analytics or crash reporting. Any such integrations are optional and clearly disclosed. We never share your personal files or search queries with third-party services.'
    },
    {
      id: 'user-rights',
      title: 'Your Rights',
      content: 'You have the right to access, modify, or delete any data that Sentivo has indexed. You can clear the search index at any time through the application settings. You also have the right to uninstall Sentivo and remove all associated data.'
    },
    {
      id: 'contact',
      title: 'Contact Us',
      content: 'If you have questions about this privacy policy or how we handle your data, please contact us at sentivo.contact@gmail.com. We will respond to your inquiries within a reasonable timeframe.'
    }
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
        
        <div className="prose prose-lg max-w-none">
          <p className="text-gray-600 mb-8">
            Last updated: 10/12/2025
          </p>
          
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-8">
            <p className="text-blue-700">
              <strong>Our Privacy Commitment:</strong> Sentivo is designed with privacy as a core principle. 
              Your data stays on your device. We don't track you. We don't sell your data. Ever.
            </p>
          </div>

          <div className="space-y-4">
            {sections.map((section) => (
              <div key={section.id} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full px-6 py-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors flex justify-between items-center"
                >
                  <h3 className="text-lg font-semibold text-gray-900">{section.title}</h3>
                  <svg
                    className={`w-5 h-5 text-gray-500 transform transition-transform ${
                      expandedSection === section.id ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {expandedSection === section.id && (
                  <div className="px-6 py-4 bg-white">
                    <p className="text-gray-600">{section.content}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Changes to This Policy</h3>
            <p className="text-gray-600 mb-4">
              We may update this privacy policy from time to time. When we do, we will revise the "last updated" date at the top of this policy. We encourage you to review this policy periodically for any changes.
            </p>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-4 mt-8">Questions or Concerns</h3>
            <p className="text-gray-600">
              If you have any questions or concerns about this privacy policy, please don't hesitate to contact us at sentivo.contact@gmail.com.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
